import { PropertyImageGallery } from "./PropertyImageGallery";
import { PropertyLocationDisplay } from "./PropertyLocationDisplay";
import { PropertyKeyStats } from "./PropertyKeyStats";
import { PropertyFinancialInfo } from "./PropertyFinancialInfo";
import { PropertyDetailTabs } from "./PropertyDetailTabs";
import { PropertyVideoPlayer } from "./PropertyVideoPlayer";
import { getEffectiveStatus } from "@/utils/propertyStatus";

type PropertyDetails = {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  arv_value: number | null;
  gross_margin: number | null;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  lot_size: string | null;
  year_built: number | null;
  foundation_type: string | null;
  roof_age: string | null;
  windows_age: string | null;
  window_type: string | null;
  furnace_age: string | null;
  water_heater_age: string | null;
  architecture_style: string | null;
  property_type: string | null;
  units?: number | null;


  parking_type: string | null;
  description: string | null;
  zillow_link: string | null;
  status: string;
  created_at: string | null;
  contract_expiration_date: string | null;
  video_url: string | null;
  video_thumbnail_url: string | null;
  closed_date: string | null;
  sale_price: number | null;
  financing_type: string | null;
}

interface PropertyDetailContentProps {
  property: PropertyDetails;
  propertyImages: string[];
  daysOnSite: number;
  calculatedGrossMargin: number | null;
  formatCurrency: (amount: number | null | undefined) => string;
  formatDate: (dateString: string | null | Date) => string;
  hideContractExpiration?: boolean;
  displayAddress?: string;
  totalImageCount?: number;
  hasMoreImages?: boolean;
  onLoadMoreImages?: () => void;
  isLoadingMoreImages?: boolean;
  showViewCount?: boolean;
  isAuthenticated?: boolean;
  hideGrossMargin?: boolean;
}

export const PropertyDetailContent = ({
  property,
  propertyImages,
  daysOnSite,
  calculatedGrossMargin,
  formatCurrency,
  formatDate,
  hideContractExpiration = false,
  displayAddress,
  totalImageCount,
  hasMoreImages,
  onLoadMoreImages,
  isLoadingMoreImages,
  showViewCount = false,
  isAuthenticated = true,
  hideGrossMargin = false
}: PropertyDetailContentProps) => {
  // Calculate effective status based on closed date and contract expiration
  const effectiveStatus = getEffectiveStatus(
    property.status,
    property.closed_date,
    property.contract_expiration_date
  );

  return (
    <div className="lg:col-span-2 space-y-6">
      {/* Image gallery with video as first item */}
      <div data-tour="property-gallery">
      <PropertyImageGallery
        propertyImages={propertyImages} 
        propertyAddress={displayAddress || property.address}
        propertyStatus={effectiveStatus}
        propertyId={property.id}
        videoUrl={property.video_url}
        videoThumbnailUrl={property.video_thumbnail_url}
        totalImageCount={totalImageCount}
        hasMoreImages={hasMoreImages}
        onLoadMore={onLoadMoreImages}
        isLoadingMore={isLoadingMoreImages}
        showViewCount={showViewCount}
        isAuthenticated={isAuthenticated}
      />
      </div>
      
      {/* Location */}
      <PropertyLocationDisplay 
        address={displayAddress || property.address}
        city={property.city}
        state={property.state}
        zipCode={property.zip_code}
      />
      
      {/* Key stats */}
      <div data-tour="property-info">
      <PropertyKeyStats
        bedrooms={property.bedrooms}
        bathrooms={property.bathrooms}
        squareFeet={property.square_feet}
        yearBuilt={property.year_built}
      />
      </div>
      
      {/* Financial info */}
      <div data-tour="property-pricing">
      <PropertyFinancialInfo
        price={property.price}
        arvValue={property.arv_value}
        grossMargin={calculatedGrossMargin}
        contractExpirationDate={hideContractExpiration ? null : property.contract_expiration_date}
        daysOnSite={daysOnSite}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        status={property.status}
        closedDate={property.closed_date}
        salePrice={property.sale_price}
        financingType={property.financing_type}
        hideGrossMargin={hideGrossMargin}
      />
      </div>
      
      {/* Tabs for description and details */}
      <PropertyDetailTabs 
        propertyType={property.property_type}
        units={property.units}

        foundationType={property.foundation_type}
        lotSize={property.lot_size}
        parkingType={property.parking_type}
        roofAge={property.roof_age}
        windowType={property.window_type}
        furnaceAge={property.furnace_age}
        waterHeaterAge={property.water_heater_age}
        architectureStyle={property.architecture_style}
        description={property.description}
      />
    </div>
  );
};
